import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsApi } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import clsx from 'clsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const TYPE_LABELS: Record<string, string> = {
  Bugfix: '버그수정',
  Enhancement: '개선',
  Feature: '신규기능',
  Support: '지원',
  Other: '기타',
};

const STATUS_LABELS: Record<string, string> = {
  OPEN: '대기',
  IN_PROGRESS: '진행중',
  REVIEW: '검토',
  DONE: '완료',
  CLOSED: '종료',
};

function MonthSelector({
  year,
  month,
  onChange,
}: {
  year: number;
  month: number;
  onChange: (year: number, month: number) => void;
}) {
  const handlePrev = () => {
    if (month === 1) {
      onChange(year - 1, 12);
    } else {
      onChange(year, month - 1);
    }
  };

  const handleNext = () => {
    if (month === 12) {
      onChange(year + 1, 1);
    } else {
      onChange(year, month + 1);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <button onClick={handlePrev} className="btn-ghost px-3">
        &larr;
      </button>
      <span className="text-lg font-semibold min-w-[120px] text-center">
        {year}년 {month}월
      </span>
      <button onClick={handleNext} className="btn-ghost px-3">
        &rarr;
      </button>
    </div>
  );
}

export default function ReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['report', 'monthly', year, month],
    queryFn: () => reportsApi.monthly(year, month),
  });

  const { data: trendData } = useQuery({
    queryKey: ['report', 'trend'],
    queryFn: () => reportsApi.trend(6),
  });

  const { data: slaData } = useQuery({
    queryKey: ['report', 'sla', year, month],
    queryFn: () => reportsApi.sla(year, month),
  });

  const closeMonthMutation = useMutation({
    mutationFn: () => reportsApi.closeMonth(year, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report'] });
      alert('월 마감이 완료되었습니다.');
    },
  });

  const report = reportData?.data;
  const trend = trendData?.data || [];
  const sla = slaData?.data;

  const handleMonthChange = (y: number, m: number) => {
    setYear(y);
    setMonth(m);
  };

  const handleCloseMonth = () => {
    if (confirm(`${year}년 ${month}월을 마감하시겠습니까?\n마감 후 이월 시간이 다음 달로 이전됩니다.`)) {
      closeMonthMutation.mutate();
    }
  };

  const handleExportCSV = () => {
    if (!report) return;

    const lines = [
      ['월간 리포트', `${year}년 ${month}월`],
      [],
      ['구분', '값'],
      ['총 요청', report.requests.total],
      ['완료', report.requests.completed],
      ['진행중', report.requests.inProgress],
      ['대기', report.requests.open],
      [],
      ['시간 현황', ''],
      ['기본 포함', report.hours.included],
      ['이월 입력', report.hours.rolloverIn],
      ['가용 시간', report.hours.available],
      ['사용 시간', report.hours.used],
      ['잔여 시간', report.hours.remaining],
      ['초과 시간', report.hours.overtime],
      ['이월 출력', report.hours.rolloverOut],
      [],
      ['요청별 작업 내역', '', '', '', '', ''],
      ['REQ-ID', '제목', '고객', '상태', '작업시간(h)', '작업자'],
      ...report.workedRequests.map((r: any) => [
        r.reqId,
        `"${r.title}"`,
        r.client || '-',
        STATUS_LABELS[r.status] || r.status,
        r.monthHours,
        r.workers || '-',
      ]),
      [],
      ['완료된 요청', '', '', '', '', ''],
      ['REQ-ID', '제목', '유형', '담당자', '예상(h)', '실제(h)'],
      ...report.completedRequests.map((r: any) => [
        r.reqId,
        `"${r.title}"`,
        r.type,
        r.assigneeName || '-',
        r.estimatedHours || '-',
        r.actualHours || '-',
      ]),
    ];

    const csv = lines.map((row) => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩중...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">리포트</h1>
        <div className="flex items-center gap-4">
          <MonthSelector year={year} month={month} onChange={handleMonthChange} />
          <div className="flex gap-2">
            <button onClick={handleExportCSV} className="btn-secondary">
              CSV 내보내기
            </button>
            {user?.role === 'ADMIN' && (
              <button
                onClick={handleCloseMonth}
                className="btn-primary"
                disabled={closeMonthMutation.isPending}
              >
                {closeMonthMutation.isPending ? '처리중...' : '월 마감'}
              </button>
            )}
          </div>
        </div>
      </div>

      {report && (
        <>
          {/* 요약 카드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card p-4">
              <p className="text-sm text-gray-500">총 요청</p>
              <p className="text-3xl font-bold text-gray-900">{report.requests.total}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-gray-500">완료</p>
              <p className="text-3xl font-bold text-green-600">{report.requests.completed}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-gray-500">사용 시간</p>
              <p className="text-3xl font-bold text-primary-600">{report.hours.used.toFixed(1)}h</p>
              <p className="text-xs text-gray-500">/ {report.hours.available}h</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-gray-500">이월 출력</p>
              {report.isClosed ? (
                <p className="text-3xl font-bold text-blue-600">{report.hours.rolloverOut}h</p>
              ) : (
                <p className="text-xl font-medium text-gray-400">미마감</p>
              )}
            </div>
          </div>

          {/* 시간 상세 */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="font-semibold mb-4">시간 현황</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">기본 포함 시간</span>
                  <span className="font-medium">{report.hours.included}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">이월 입력</span>
                  <span className="font-medium text-blue-600">+{report.hours.rolloverIn}h</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-700 font-medium">가용 시간</span>
                  <span className="font-bold">{report.hours.available}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">사용 시간</span>
                  <span className="font-medium text-primary-600">-{report.hours.used.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-700 font-medium">잔여 시간</span>
                  <span className={clsx(
                    'font-bold',
                    report.hours.remaining > 0 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {report.hours.remaining.toFixed(1)}h
                  </span>
                </div>
                {report.hours.overtime > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>초과 시간</span>
                    <span className="font-bold">+{report.hours.overtime.toFixed(1)}h</span>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">요율별 시간</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">일반</span>
                    <span>{report.hours.byRate.normal}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-600">야간긴급 (1.5x)</span>
                    <span>{report.hours.byRate.urgentNight}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-600">휴일긴급 (2.0x)</span>
                    <span>{report.hours.byRate.urgentHoliday}h</span>
                  </div>
                  {report.hours.warranty > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>보증 (미청구)</span>
                      <span>{report.hours.warranty}h</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 담당자별 */}
            <div className="card p-6">
              <h3 className="font-semibold mb-4">담당자별 작업</h3>
              {report.byAssignee.filter((a: any) => a.hours > 0).length === 0 ? (
                <p className="text-gray-500 text-center py-8">데이터 없음</p>
              ) : (
                <div className="space-y-3">
                  {report.byAssignee
                    .filter((a: any) => a.hours > 0)
                    .map((assignee: any) => (
                      <div key={assignee.id} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-medium text-sm">
                          {assignee.name[0]}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{assignee.name}</p>
                          <p className="text-xs text-gray-500">{assignee.requestCount}건 처리</p>
                        </div>
                        <span className="font-semibold text-primary-600">{assignee.hours}h</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* 트렌드 차트 */}
          {trend.length > 0 && (
            <div className="card p-6">
              <h3 className="font-semibold mb-4">월별 트렌드 (최근 6개월)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trend}>
                  <XAxis dataKey="label" />
                  <YAxis yAxisId="left" orientation="left" stroke="#3B82F6" />
                  <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="hours" fill="#3B82F6" name="작업시간(h)" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="completed" fill="#10B981" name="완료건수" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* 유형별 분포 */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="font-semibold mb-4">유형별 분포</h3>
              {report.byType.length === 0 ? (
                <p className="text-gray-500 text-center py-8">데이터 없음</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={report.byType.map((t: any) => ({
                        name: TYPE_LABELS[t.type] || t.type,
                        value: t.count,
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value, cx, cy, midAngle = 0, outerRadius }) => {
                        const RADIAN = Math.PI / 180;
                        const radius = (outerRadius as number) + 25;
                        const x = (cx as number) + radius * Math.cos(-midAngle * RADIAN);
                        const y = (cy as number) + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill="#374151"
                            textAnchor={x > (cx as number) ? 'start' : 'end'}
                            dominantBaseline="central"
                            fontSize={12}
                          >
                            {`${name} (${value})`}
                          </text>
                        );
                      }}
                      labelLine={true}
                    >
                      {report.byType.map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* SLA */}
            {sla && (
              <div className="card p-6">
                <h3 className="font-semibold mb-4">SLA 준수율</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">응답 SLA</p>
                    <p className={clsx(
                      'text-3xl font-bold',
                      sla.summary.response.rate >= 90 ? 'text-green-600' :
                      sla.summary.response.rate >= 70 ? 'text-yellow-600' : 'text-red-600'
                    )}>
                      {sla.summary.response.rate}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {sla.summary.response.met}/{sla.summary.total}건 준수
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">착수 SLA</p>
                    <p className={clsx(
                      'text-3xl font-bold',
                      sla.summary.start.rate >= 90 ? 'text-green-600' :
                      sla.summary.start.rate >= 70 ? 'text-yellow-600' : 'text-red-600'
                    )}>
                      {sla.summary.start.rate}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {sla.summary.start.met}/{sla.summary.total}건 준수
                    </p>
                  </div>
                </div>
                {sla.violations.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-red-600 font-medium mb-2">
                      SLA 위반 {sla.violations.length}건
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      {sla.violations.slice(0, 3).map((v: any) => (
                        <p key={v.id}>{v.reqId} - {v.title}</p>
                      ))}
                      {sla.violations.length > 3 && (
                        <p>외 {sla.violations.length - 3}건...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 요청별 작업 내역 */}
          <div className="card">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">요청별 작업 내역 ({report.workedRequests?.length || 0}건)</h3>
              <p className="text-xs text-gray-500 mt-1">해당 월에 작업기록이 있는 모든 요청 (커뮤니케이션 포함)</p>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>REQ-ID</th>
                    <th>제목</th>
                    <th>고객</th>
                    <th>상태</th>
                    <th>작업시간</th>
                    <th>작업건수</th>
                    <th>작업자</th>
                  </tr>
                </thead>
                <tbody>
                  {(!report.workedRequests || report.workedRequests.length === 0) ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-gray-500">
                        작업 기록이 없습니다
                      </td>
                    </tr>
                  ) : (
                    report.workedRequests.map((r: any) => (
                      <tr key={r.id} className={r.reqId?.endsWith('-000') ? 'bg-blue-50' : ''}>
                        <td className="text-primary-600 font-medium">{r.reqId}</td>
                        <td>{r.title}</td>
                        <td className="text-gray-600">{r.client || '-'}</td>
                        <td>
                          <span className={clsx(
                            'px-2 py-0.5 text-xs rounded-full',
                            r.status === 'DONE' || r.status === 'CLOSED' ? 'bg-green-100 text-green-700' :
                            r.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                            r.status === 'REVIEW' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          )}>
                            {STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                        <td className="font-medium">{r.monthHours}h</td>
                        <td className="text-gray-500">{r.logCount}건</td>
                        <td className="text-gray-600 text-sm">{r.workers || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {report.workedRequests && report.workedRequests.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2">
                      <td colSpan={4} className="text-right font-medium text-gray-700">합계</td>
                      <td className="font-bold text-primary-600">
                        {report.workedRequests.reduce((sum: number, r: any) => sum + (r.monthHours || 0), 0).toFixed(1)}h
                      </td>
                      <td className="text-gray-500">
                        {report.workedRequests.reduce((sum: number, r: any) => sum + (r.logCount || 0), 0)}건
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* 완료된 요청 목록 */}
          <div className="card">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">완료된 요청 ({report.completedRequests.length}건)</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>REQ-ID</th>
                    <th>제목</th>
                    <th>유형</th>
                    <th>담당자</th>
                    <th>예상</th>
                    <th>실제</th>
                  </tr>
                </thead>
                <tbody>
                  {report.completedRequests.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-gray-500">
                        완료된 요청이 없습니다
                      </td>
                    </tr>
                  ) : (
                    report.completedRequests.map((r: any) => (
                      <tr key={r.id}>
                        <td className="text-primary-600 font-medium">{r.reqId}</td>
                        <td>{r.title}</td>
                        <td>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100">
                            {TYPE_LABELS[r.type] || r.type}
                          </span>
                        </td>
                        <td className="text-gray-600">{r.assigneeName || '-'}</td>
                        <td className="text-gray-500">{r.estimatedHours || '-'}h</td>
                        <td className="font-medium">{r.actualHours || 0}h</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
